import { useCallback, useEffect, useState } from 'react';
import './HelpPanel.css';

/**
 * "How this works" — the launcher pill and right drawer APR uses on twenty screens.
 *
 * MODELLED ON `apr/compat/HelpPanel.tsx`, not imported from it: products share no code
 * between their folders (CLAUDE.md §1.7), and anything genuinely common is promoted to the
 * shared layer deliberately rather than by one product reaching into another's directory.
 * If a third product needs this, that is the moment to promote it.
 *
 * The content model is APR's — `purpose`, `doHere`, `fits`, `firstTime`, `sections` — so a
 * user who has read APR's help knows how to read this one.
 *
 * WHAT CHANGED from my first attempt: an inline numbered block sitting above the content.
 * It took vertical space on every visit for something you read once, and numbering implied
 * a sequence where there was none. This is closed by default and costs nothing until asked
 * for.
 */

export interface HelpDefItem {
  term: string;
  color?: string;
  /** HTML. */
  def: string;
}

export interface HelpSection {
  label?: string;
  items: HelpDefItem[];
  /** HTML. */
  note?: string;
}

export interface Help {
  title: string;
  /** An emoji, as APR does. */
  icon?: string;
  /** One or two sentences on what the screen is FOR — not what it contains. HTML. */
  purpose: string;
  /** Imperatives, verb first, affordances in <b>. Never numbered. HTML per item. */
  doHere?: string[];
  /** The breadcrumb, with &rarr;. */
  fits?: string;
  /** One sentence of first-run advice. HTML. */
  firstTime?: string;
  sections?: HelpSection[];
}

export function HelpPanel({ help }: { help: Help }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  return (
    <>
      <button
        type="button"
        className="shlp-launch"
        onClick={() => setOpen((o) => !o)}
        title="How this works"
      >
        <span className="shlp-q">?</span> How this works
      </button>

      <div className={`shlp-overlay${open ? ' on' : ''}`} onClick={close} />
      <aside className={`shlp-drawer${open ? ' on' : ''}`} role="dialog" aria-label="How this works">
        <div className="shlp-head">
          <div className="shlp-title">
            <span className="shlp-ic">{help.icon ?? 'ℹ️'}</span>
            <span>{help.title}</span>
          </div>
          <button className="shlp-x" onClick={close} aria-label="Close">
            ×
          </button>
        </div>

        <div className="shlp-body">
          <p className="shlp-purpose" dangerouslySetInnerHTML={{ __html: help.purpose }} />

          {help.doHere?.length ? (
            <>
              <div className="shlp-lbl">What you do here</div>
              <ul className="shlp-list">
                {help.doHere.map((d, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: d }} />
                ))}
              </ul>
            </>
          ) : null}

          {help.fits ? (
            <>
              <div className="shlp-lbl">Where it fits</div>
              <div className="shlp-fits" dangerouslySetInnerHTML={{ __html: help.fits }} />
            </>
          ) : null}

          {help.firstTime ? (
            <div className="shlp-first">
              <span className="shlp-first-ic">▶</span>
              <div>
                <div className="shlp-first-lbl">First time? Do this</div>
                <div
                  className="shlp-first-txt"
                  dangerouslySetInnerHTML={{ __html: help.firstTime }}
                />
              </div>
            </div>
          ) : null}

          {help.sections?.map((sec, si) => (
            <div key={si}>
              {sec.label ? <div className="shlp-lbl">{sec.label}</div> : null}
              <div className="shlp-defs">
                {sec.items.map((it, ii) => (
                  <div className="shlp-def" key={ii}>
                    <span
                      className="shlp-def-term"
                      style={
                        it.color
                          ? {
                              background: `${it.color}24`,
                              color: it.color,
                              border: `1px solid ${it.color}`,
                            }
                          : undefined
                      }
                    >
                      {it.term}
                    </span>
                    <span className="shlp-def-txt" dangerouslySetInnerHTML={{ __html: it.def }} />
                  </div>
                ))}
              </div>
              {sec.note ? (
                <p className="shlp-note" dangerouslySetInnerHTML={{ __html: sec.note }} />
              ) : null}
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}
